import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, Type } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';

const AudioPlayer = ({ text, audioUrl, onWordHighlight }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [highlightEnabled, setHighlightEnabled] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const wordsRef = useRef([]);

  // Parse words for highlighting
  useEffect(() => {
    if (text) {
      wordsRef.current = text.split(/\s+/);
    }
  }, [text]);

  // Reset highlight when audio stops
  useEffect(() => {
    if (!isPlaying && onWordHighlight) {
      onWordHighlight(-1);
    }
  }, [isPlaying, onWordHighlight]);

  // Only show player if audioUrl is provided (user uploaded audio)
  if (!audioUrl) {
    return null;
  }

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const currentProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(currentProgress);
      setCurrentTime(audioRef.current.currentTime);

      // Calculate which word should be highlighted based on progress
      if (highlightEnabled && onWordHighlight && wordsRef.current.length > 0 && isPlaying) {
        const wordIndex = Math.floor((currentProgress / 100) * wordsRef.current.length);
        // Clamp to valid range
        const clampedIndex = Math.min(wordIndex, wordsRef.current.length - 1);
        onWordHighlight(clampedIndex);
      }
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    if (onWordHighlight) {
      onWordHighlight(-1);
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current && audioRef.current.duration) {
      const time = (value[0] / 100) * audioRef.current.duration;
      audioRef.current.currentTime = time;
      setProgress(value[0]);
      setCurrentTime(time);
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.duration, audioRef.current.currentTime + seconds));
    }
  };

  const toggleHighlight = () => {
    setHighlightEnabled(!highlightEnabled);
    if (highlightEnabled && onWordHighlight) {
      onWordHighlight(-1);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-4 bg-card/50 backdrop-blur-sm rounded-2xl border border-border">
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onLoadedMetadata={handleLoadedMetadata}
        muted={isMuted}
      />
      
      <div className="flex items-center justify-center gap-2">
        <Button
          data-testid="audio-skip-back"
          variant="ghost"
          size="sm"
          onClick={() => skip(-10)}
          className="rounded-full"
          title="10 seconden terug"
        >
          <SkipBack className="w-4 h-4" />
        </Button>

        <Button
          data-testid="audio-play-pause"
          onClick={togglePlay}
          className="rounded-full w-14 h-14 bg-old-rose hover:bg-old-rose/80 text-night-blue shadow-lg"
        >
          {isPlaying ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 ml-1" />
          )}
        </Button>

        <Button
          data-testid="audio-skip-forward"
          variant="ghost"
          size="sm"
          onClick={() => skip(10)}
          className="rounded-full"
          title="10 seconden vooruit"
        >
          <SkipForward className="w-4 h-4" />
        </Button>

        <Button
          data-testid="audio-mute"
          variant="ghost"
          size="sm"
          onClick={() => setIsMuted(!isMuted)}
          className="rounded-full"
          title={isMuted ? 'Geluid aan' : 'Geluid uit'}
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </Button>

        <Button
          data-testid="audio-highlight-toggle"
          variant="ghost"
          size="sm"
          onClick={toggleHighlight}
          className={`rounded-full ${highlightEnabled ? 'bg-gold/20 text-gold' : ''}`}
          title={highlightEnabled ? 'Woord-markering uit' : 'Woord-markering aan'}
        >
          <Type className="w-4 h-4" />
        </Button>
      </div>

      <div className="px-2">
        <Slider
          data-testid="audio-progress"
          value={[progress]}
          onValueChange={handleSeek}
          max={100}
          step={0.1}
          className="cursor-pointer"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1 font-accent">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      <div className="text-center">
        <p className="text-sm text-muted-foreground font-accent">
          Luister naar het verhaal 🎙️
        </p>
        {highlightEnabled && (
          <p className="text-xs text-gold/70 font-accent mt-1">
            ✨ Woorden worden gemarkeerd tijdens het luisteren
          </p>
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;
